import { CityQuestStatusEnum } from '@escapenavigator/types/dist/city-quest/enum/city-quest-status.enum';
import { PartnerCityQuestRO } from '@escapenavigator/types/dist/partners-portal/partner-city-quest.ro';
import { Alert, Button, Card, Center, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { IconBook2, IconMessageCircle, IconPlus } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../api';
import { PageHeader } from '../components/page-header';
import { StoryStatusBadge } from '../components/status-badge';
import { t } from '../dictionary';

export const StoriesPage = () => {
    const [quests, setQuests] = useState<PartnerCityQuestRO[] | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        api.quests()
            .then(({ data }) => setQuests(data))
            .catch(() => setQuests([]));
    }, []);

    if (!quests) {
        return (
            <Center h={300}>
                <Loader />
            </Center>
        );
    }

    return (
        <>
            <PageHeader
                title={t.stories.title}
                subtitle={t.stories.subtitle}
                action={
                    <Button component={Link} to='/quests/new' leftSection={<IconPlus size={16} />}>
                        {t.stories.newStory}
                    </Button>
                }
            />

            {!quests.length && (
                <Card padding='xl'>
                    <Center py='xl'>
                        <Stack align='center' gap='sm' maw={420}>
                            <IconBook2
                                size={40}
                                stroke={1.2}
                                color='var(--mantine-color-indigo-5)'
                            />
                            <Title order={4}>{t.stories.empty}</Title>
                            <Text size='sm' c='dimmed' ta='center'>
                                {t.stories.emptyText}
                            </Text>
                            <Button component={Link} to='/quests/new' mt='xs'>
                                {t.stories.newStory}
                            </Button>
                        </Stack>
                    </Center>
                </Card>
            )}

            <Stack gap='sm'>
                {quests.map((quest) => {
                    const title =
                        quest.title?.en ||
                        Object.values(quest.title || {})[0] ||
                        quest.key;

                    return (
                        <Card
                            key={quest.id}
                            padding='lg'
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/quests/${quest.id}`)}
                        >
                            <Group justify='space-between' wrap='nowrap'>
                                <div style={{ minWidth: 0 }}>
                                    <Group gap='xs' wrap='nowrap'>
                                        <Text fw={650} truncate>
                                            {title}
                                        </Text>
                                    </Group>
                                    <Text size='sm' c='dimmed' mt={2}>
                                        {[
                                            quest.cityTitle,
                                            quest.language?.toUpperCase(),
                                            t.stories.steps(quest.stopsCount ?? 0),
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </Text>
                                </div>
                                <StoryStatusBadge status={quest.status} />
                            </Group>

                            {quest.status === CityQuestStatusEnum.REJECTED &&
                                quest.rejectionReason && (
                                    <Alert
                                        mt='sm'
                                        variant='light'
                                        color='red'
                                        icon={<IconMessageCircle size={16} />}
                                        title={t.stories.rejectionTitle}
                                    >
                                        {quest.rejectionReason}
                                    </Alert>
                                )}
                        </Card>
                    );
                })}
            </Stack>
        </>
    );
};
